<template>
  <div class="uk-card-body p-0 uk-grid-collapse uk-grid-large" uk-grid>
    <div class="uk-padding-small position-relative uk-width-1-5@m">
      <h5><i class="uil-table"/> {{ $t('general.add_with_excell') }}</h5>

      <!-- File Upload Area -->
      <div class="file-upload-container mb-3">
        <div :class="{'file-upload-disabled': isLoading, 'file-upload-has-file': fileInfo.file}"
             class="file-upload-area">
          <input
              id="fileToUpload"
              ref="fileInput"
              :disabled="isLoading"
              accept=".xls, .xlsx"
              class="file-upload-input"
              name="fileToUpload"
              type="file"
              @change="setFile"
              @click="onUploadClick"
          />
          <div class="file-upload-content">
            <i class="uil-cloud-upload file-upload-icon"/>
            <p class="file-upload-text">
              {{ fileInfo.file ? fileInfo.name : $t('general.excel_drag_drop') }}
            </p>
            <span v-if="fileInfo.file" class="file-info">
              {{ displayedRowCount }} {{ $t('general.survey_question') }}
            </span>
          </div>
        </div>

        <button class="btn btn-info template-download-btn" type="button" @click="exportSampleFile">
          <i class="uil-download-alt"/> {{ $t('general.download_excel_template') }}

          <i class="uil-info-circle ml-1"
             style="font-size: 1rem"
             :uk-tooltip="`title: ${$t('general.excel_template_download_info')}; pos: bottom-center`"
          />
        </button>


      </div>
    </div>

    <!-- Right Column - Data Display -->
    <div class="uk-padding-small uk-width-expand d-flex flex-column justify-content-between excel-data-side">
      <!-- Add button - displayed at top right when data is loaded -->
      <div v-if="displayedRows.length > 0" class="text-right mb-3">
        <button class="add-btn uk-button" type="button" @click="addImportedData">
          <i class="uil-plus-circle"/> {{
            is_added ? $t('general.questions_imported') : $t('general.import_questions')
          }}
        </button>
      </div>

      <default-loading v-if="isLoading"/>
      <div v-else class="uk-position-relative my-4 text-center">
        <div v-if="displayedRows.length === 0" class="empty-data-message">
          <i class="uil-file-upload-alt empty-data-icon"></i>
          <h4>{{ $t('general.add_with_excel') }}</h4>
        </div>
        <div v-else class="table-responsive">
          <table class="table align-items-center">
            <thead>
            <tr>
              <th scope="col"></th> <!-- New column for action buttons -->

              <th v-for="(header, index) in tableHeaders" :key="index" scope="col">
                {{ header }}{{ '*' }}
              </th>
              <th v-if="tableHeaders.length" scope="col">{{ $t('general.transactions') }}</th>
              <!-- New column for action buttons -->
            </tr>
            </thead>
            <tbody class="list">
            <tr v-for="(item, index) in displayedRows" :key="index">
              <td>{{ index + 1 }}</td>
              <td v-for="(header, colIndex) in tableHeaders" :key="colIndex">
                {{ item[colIndex] }}
              </td>
              <td>
                <button class="delete-btn uk-button uk-button-danger uk-button-small" type="button"
                        @click="removeItem(index)">
                  <i class="uil-trash-alt"/>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="displayedRows.length > 0" class="text-right mb-3">
        <button class="add-btn uk-button" type="button" @click="addImportedData">
          <i class="uil-plus-circle"/> {{
            is_added ? $t('general.questions_imported') : $t('general.import_questions')
          }}
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import readXlsxFile from "read-excel-file";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";
import { downloadFromUrl, exportFileName, readBlobError } from "@/utils/fileDownload";

/**
 * Native secicide MIME ile DARALTMA YAPILMIYOR — Android .xlsx'i cogu zaman
 * `application/octet-stream` olarak raporladigi icin Excel MIME'i verilince
 * dosyalar gorunur ama secilemez hale geliyor. Dogrulama JS'te, dosya adi
 * uzerinden. Web'deki <input accept> uzanti kullanmaya devam ediyor;
 * orada dogru olan o.
 */
const EXCEL_PICK_MIME = "*/*";
const EXCEL_EXTENSIONS = [".xls", ".xlsx"];

function isExcelFileName(name) {
  const value = String(name || "").toLowerCase();
  return EXCEL_EXTENSIONS.some((ext) => value.endsWith(ext));
}

export default {
  name: "ExcelUploader",
  components: {DefaultLoading},

  props: {
    // API endpoint for downloading sample Excel
    sampleExcelEndpoint: {
      type: String,
      required: true
    },
    // Default file name for downloaded sample
    sampleFileName: {
      type: String,
      default: "excel_sample"
    },
    excelHasHeader: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      errorMessages: [],
      isLoading: false,
      file: null,
      file_value: null,
      importedExam: null,
      processedRows: [], // This will hold our actual data rows
      fileInfo: {
        name: "",
        count: 0,
        file: null,
      },
      is_added: false,
      tableHeaders: [],
      _waitingForFile: false,
    };
  },

  mounted() {
    if (!bridge.isNative) {
      return;
    }
    this._unsubscribePickFile = bridge.on("PICK_FILE_RESULT", (payload) => {
      this.applyNativeFile(payload);
    });
  },

  beforeDestroy() {
    if (this._unsubscribePickFile) {
      this._unsubscribePickFile();
    }
  },

  computed: {
    //(excluding header row)
    displayedRows() {
      return this.processedRows || [];
    },

    displayedRowCount() {
      this.is_added = false
      return this.displayedRows.length;
    }
  },

  methods: {
    resetFileInfo() {
      this.is_added = false;
      this.fileInfo.file = null;
      this.importedExam = null;
      this.processedRows = [];
      this.tableHeaders = [];

      // Use vue_refs instead of document.getElementById, better solutıion
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    setFile(e) {
      this.file_value = e.target.value;
      this.file = e.target.files[0];
      this.excelToJSON();
    },

    // RN WebView'da <input type="file"> dosya seciciyi acmiyor;
    // secim native tarafa devrediliyor (PICK_FILE / PICK_FILE_RESULT).
    onUploadClick(event) {
      if (!bridge.isNative || this.isLoading) {
        return;
      }
      event.preventDefault();
      this._waitingForFile = true;
      native.pickFile(EXCEL_PICK_MIME);
    },

    applyNativeFile(payload) {
      if (!this._waitingForFile) return;
      this._waitingForFile = false;
      if (!payload || !payload.success || !payload.file) return;

      const { name, type, base64 } = payload.file;

      if (!isExcelFileName(name)) {
        this.errorMessages.push(this.$t("general.invalid_file_type"));
        return;
      }
      const byteString = atob(base64);
      const buffer = new ArrayBuffer(byteString.length);
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < byteString.length; i++) {
        bytes[i] = byteString.charCodeAt(i);
      }

      this.file_value = name;
      this.file = new File([buffer], name, { type });
      this.excelToJSON();
    },

    excelToJSON() {
      if (this.file) {
        this.isLoading = true;
        const fileName = this.file_value ? this.file_value.split(/(\\|\/)/g).pop() : "";

        this.fileInfo.file = this.file;
        readXlsxFile(this.file).then((rows) => {
          if (rows.length < 2) {
            this.errorMessages.push(this.$t("general.excel_empty_or_invalid"));
            this.$emit('import-error', this.$t("general.excel_empty_or_invalid"));
            this.isLoading = false;
            return;
          }

          if (this.excelHasHeader) {
            this.tableHeaders = rows[0];
            this.processedRows = rows.slice(1).filter(row => row && row.length > 0);
          } else {
            this.processedRows = rows
          }

          // Store full data for other operations if needed
          this.importedExam = rows;

          this.fileInfo.name = fileName;
          this.fileInfo.count = this.processedRows.length;
          this.is_added = false;
          this.isLoading = false;

        }).catch(error => {
          this.errorMessages.push(this.$t("general.excel_read_error"));
          this.$emit('import-error', error);
          this.isLoading = false;
        });
      }
    },

    removeItem(index) {
      this.processedRows.splice(index, 1);

      this.fileInfo.count = this.processedRows.length;

      if (this.processedRows.length === 0) {
        this.resetFileInfo();
      }
    },

    addImportedData() {
      this.$emit('imported', this.processedRows);

      this.is_added = true;

      this.$emit('add-success', {
        message: `${this.processedRows.length} kayıt başarıyla eklendi.`
      });
    },

    // Indirme web/native ayrimi utils/fileDownload.js icinde
    async exportSampleFile(event) {
      event.preventDefault();

      if (this.isLoading) {
        return;
      }
      this.isLoading = true;

      try {
        await downloadFromUrl({
          url: this.sampleExcelEndpoint,
          headers: this.apiHeaders,
          fileName: exportFileName(this.sampleFileName),
        });
      } catch (error) {
        this.errorMessages.push(await readBlobError(error));
        this.$emit('download-error', error);
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.file-upload-container {
  width: 100%;
  margin-bottom: 20px;
}

.file-upload-area {
  position: relative;
  border: 2px dashed #ccc;
  border-radius: 6px;
  padding: 30px 20px;
  text-align: center;
  background-color: #f8f9fa;
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 15px;
  overflow: hidden;
}

.file-upload-area:hover {
  border-color: #ae71ff;
  background-color: #f5f0ff;
}

.file-upload-disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.file-upload-has-file {
  border-color: #28a745;
  background-color: #f0fff4;
}

.file-upload-input {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
}

.file-upload-content {
  position: relative;
  z-index: 1;
}

/* New styles for buttons */
.delete-btn {
  font-size: 0.85rem;
}

.delete-btn:hover {
  color: white;
}

.add-btn {
  padding: 6px 15px;
  font-size: 0.95rem;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.add-btn:hover {
  background-color: #218838;
}

.file-upload-text {
  word-wrap: break-word;
}

.empty-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #ddd;
  margin: 20px 0;
  position: relative;
}

.empty-data-icon {
  font-size: 4rem;
  color: #5864FF;
  margin-bottom: 15px;
}

.empty-data-message h4 {
  color: #343a40;
  margin-bottom: 10px;
  font-weight: 600;
}

.empty-data-message p {
  color: #6c757d;
  text-align: center;
  line-height: 1.5;
}

/* Sag kolon ayraci: inline `border-left` idi, mobilde kolonlar alt alta
   dustugu icin anlamsizdi ve media query yazilamiyordu. */
.excel-data-side {
  border-left: 1px solid #ccc;
}

@media (max-width: 959px) {
  /* uk-width-1-5@m ile kolonlar zaten alt alta duser; dikey ayraci
     yatay ayraca cevir */
  .excel-data-side {
    border-left: 0;
    border-top: 1px solid #ccc;
  }

  .file-upload-area {
    padding: 20px 12px;
  }

  /* MOBILE_UI_GUIDELINES: dokunma hedefi 48px, Body 16px */
  .template-download-btn,
  .add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 48px;
    font-size: 16px;
    line-height: 1.4;
  }

  .empty-data-message {
    padding: 40px 16px;
    margin: 12px 0;
  }

  .empty-data-icon {
    font-size: 3rem;
  }

  /* Tablo mobilde sikistirilmaz, yatay kaydirilir */
  .table-responsive {
    -webkit-overflow-scrolling: touch;
  }

  .table-responsive .table {
    min-width: 520px;
  }

  .table-responsive .table th,
  .table-responsive .table td {
    white-space: nowrap;
    font-size: 16px;
  }

  .delete-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
  }
}
</style>